import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Key, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft, Loader } from 'lucide-react';
import zxcvbn from 'zxcvbn';
import { updateUserPassword } from '../lib/auth';

function ResetPasswordPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [hash, setHash] = useState<string | null>(null);
  const [validatingSession, setValidatingSession] = useState(true);

  useEffect(() => {
    // Get the hash from the URL
    const hashFragment = window.location.hash;
    if (hashFragment) {
      setHash(hashFragment);
      setValidatingSession(false);
    } else {
      // If no hash is present, check if this is a valid reset session
      const checkSession = async () => {
        try {
          const { data, error } = await supabase.auth.getSession();
          if (error || !data.session) {
            setError('Invalid or expired password reset link. Please request a new password reset.');
          }
          setValidatingSession(false);
        } catch (err) {
          console.error('Error checking session:', err);
          setError('Failed to validate your session. Please try again or request a new password reset.');
          setValidatingSession(false);
        }
      };
      checkSession();
    }

    // Extract access token from URL if present
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    
    if (accessToken) {
      // Set the access token in the session
      const setAccessToken = async () => {
        try {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: '',
          });
          
          if (error) {
            console.error('Error setting session:', error);
            setError('Invalid or expired reset link. Please request a new password reset.');
          }
          
          setValidatingSession(false);
        } catch (err) {
          console.error('Error setting access token:', err);
          setError('Failed to process your reset link. Please request a new password reset.');
          setValidatingSession(false);
        }
      };
      
      setAccessToken();
    }
  }, [location]);

  const checkPasswordStrength = (password: string) => {
    const result = zxcvbn(password);
    setPasswordStrength(result.score);
    return result.score >= 3;
  };

  const getPasswordFeedback = () => {
    if (!password) return null;
    const strengthColors = ['red', 'orange', 'yellow', 'lime', 'green'];
    const strengthTexts = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
    
    return (
      <div className="mt-1">
        <div className="flex gap-1 mb-1">
          {[0, 1, 2, 3, 4].map((score) => (
            <div
              key={score}
              className={`h-2 w-full rounded-full ${
                score <= passwordStrength
                  ? `bg-${strengthColors[passwordStrength]}-500`
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className={`text-xs text-${strengthColors[passwordStrength]}-600`}>
          {strengthTexts[passwordStrength]}
        </p>
      </div>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!checkPasswordStrength(password)) {
      setError('Please choose a stronger password. Use a mix of letters, numbers, and symbols.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { success, error } = await updateUserPassword(password);

      if (!success) {
        throw new Error(error || 'Failed to update password');
      }

      setSuccess(true);
      
      // Clear the form
      setPassword('');
      setConfirmPassword('');
      
      // Redirect after a delay
      setTimeout(() => {
        navigate('/auth');
      }, 5000);
    } catch (err) {
      console.error('Password reset error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  if (validatingSession) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Validating Your Request</h2>
          <p className="text-gray-600">Please wait while we verify your password reset link...</p>
        </div>
      </div>
    );
  }

  if (error && !hash) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl md:text-2xl font-bold mb-4">Reset Link Invalid</h1>
            <p className="text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/auth')}
              className="w-full py-2 px-4 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl md:text-2xl font-bold mb-4">Password Reset Successful</h1>
            <p className="text-gray-600 mb-6">
              Your password has been successfully reset. You will be redirected to the login page in a few seconds.
            </p>
            <button
              onClick={() => navigate('/auth')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
            >
              Sign In Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 to-white py-12 px-4">
      <div className="max-w-md mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold text-center mb-8">Reset Your Password</h1>

        <div className="bg-white p-6 md:p-8 rounded-lg shadow-lg">
          {error && (
            <div className="mb-6 p-4 text-sm text-red-700 bg-red-100 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="flex items-center mb-4">
              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="flex items-center text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                <span>Back to Sign In</span>
              </button>
            </div>
            
            <p className="text-gray-600 mb-4">
              Create a new password for your account. Choose a strong password that you haven't used before.
            </p>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    checkPasswordStrength(e.target.value);
                  }}
                  className="pl-10 pr-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {getPasswordFeedback()}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Key className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="pl-10 pr-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center">
                  <Loader className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" />
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordPage;